import { useState, useEffect, useCallback } from 'react';
import { MCPModel, UseModelsState } from '../types';
import { apiService, APIError } from '../services/api';

export const useModels = () => {
  const [state, setState] = useState<UseModelsState>({
    models: [],
    loading: false,
    error: null,
  });

  const loadModels = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const models = await apiService.getModels();
      setState(prev => ({
        ...prev,
        models,
        loading: false,
      }));
    } catch (error) {
      const apiError =
        error instanceof APIError
          ? error
          : new APIError('Error al cargar modelos');
      setState(prev => ({
        ...prev,
        loading: false,
        error: apiError,
      }));
    }
  }, []);

  const createModel = useCallback(
    async (modelData: Omit<MCPModel, 'id' | 'progress'>) => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const newModel = await apiService.createModel(modelData);
        setState(prev => ({
          ...prev,
          models: [...prev.models, newModel],
          loading: false,
        }));
        return newModel;
      } catch (error) {
        const apiError =
          error instanceof APIError
            ? error
            : new APIError('Error al crear modelo');
        setState(prev => ({
          ...prev,
          loading: false,
          error: apiError,
        }));
        return null;
      }
    },
    []
  );

  const updateModel = useCallback(
    async (id: string, updates: Partial<MCPModel>) => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const updatedModel = await apiService.updateModel(id, updates);
        setState(prev => ({
          ...prev,
          models: prev.models.map(model =>
            model.id === updatedModel.id ? updatedModel : model
          ),
          loading: false,
        }));
        return updatedModel;
      } catch (error) {
        const apiError =
          error instanceof APIError
            ? error
            : new APIError('Error al actualizar modelo');
        setState(prev => ({
          ...prev,
          loading: false,
          error: apiError,
        }));
        return null;
      }
    },
    []
  );

  const deleteModel = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      await apiService.deleteModel(id);
      setState(prev => ({
        ...prev,
        models: prev.models.filter(model => model.id !== id),
        loading: false,
      }));
      return true;
    } catch (error) {
      const apiError =
        error instanceof APIError
          ? error
          : new APIError('Error al eliminar modelo');
      setState(prev => ({
        ...prev,
        loading: false,
        error: apiError,
      }));
      return false;
    }
  }, []);

  const deployModel = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const deployedModel = await apiService.deployModel(id);
      setState(prev => ({
        ...prev,
        models: prev.models.map(model =>
          model.id === deployedModel.id ? deployedModel : model
        ),
        loading: false,
      }));
      return deployedModel;
    } catch (error) {
      const apiError =
        error instanceof APIError
          ? error
          : new APIError('Error al desplegar modelo');
      setState(prev => ({
        ...prev,
        loading: false,
        error: apiError,
      }));
      return null;
    }
  }, []);

  const getModelById = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const model = await apiService.getModelById(id);
      setState(prev => ({ ...prev, loading: false }));
      return model;
    } catch (error) {
      const apiError =
        error instanceof APIError
          ? error
          : new APIError('Error al cargar modelo');
      setState(prev => ({
        ...prev,
        loading: false,
        error: apiError,
      }));
      return null;
    }
  }, []);

  const refreshModels = useCallback(() => {
    loadModels();
  }, [loadModels]);

  const getModelsByStatus = useCallback(
    (status: string) => {
      return state.models.filter(model => model.status === status);
    },
    [state.models]
  );

  const getTrainingModels = useCallback(() => {
    return state.models.filter(model => model.status === 'training');
  }, [state.models]);

  const getDeployedModels = useCallback(() => {
    return state.models.filter(model => model.status === 'deployed');
  }, [state.models]);

  // Cargar datos iniciales
  useEffect(() => {
    loadModels();
  }, []);

  return {
    ...state,
    createModel,
    updateModel,
    deleteModel,
    deployModel,
    getModelById,
    refreshModels,
    getModelsByStatus,
    getTrainingModels,
    getDeployedModels,
  };
};
